import './UserPlanWidget.scss';
import { useTranslator } from '@Localization';
import { ColorPaletteProp, LinearProgress } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_Card } from '../../../../design/card/card/S_Cards';
import { SpaceBetweenColumnLayout } from '../../../../modules/common/layouts/spaceBetweenColumnLayout/SpaceBetweenColumnLayout';
import { IPlanInfo, Plan } from '../user.store';
import { AccountInfinityLimit } from './AccountInfinityLimit';

interface IUserPlanWidgetProps {
  planInfo: IPlanInfo;
  usedLimits: {
    usedSpace: number;
    workspaces: number;
    workspaceUsers: number;
  };
}

const renderLimit = (limit: number): ReactElement => (limit < 0 ? <AccountInfinityLimit /> : <span>{limit}</span>);

const LimitConsumption = ({ used, limit }: { used: number; limit: number }): ReactElement => {
  const getColor = (): ColorPaletteProp => {
    if (used >= limit) {
      return 'danger';
    }
    if (used / limit > 0.8) {
      return 'warning';
    }
    return 'primary';
  };

  return (
    <div className={`limit-consumption ${limit >= 0 && used > limit ? 'exceeded' : ''}`}>
      <div className='limit-consumption-label'>
        {used}/{renderLimit(limit)}
      </div>
      {limit >= 0 && (
        <LinearProgress
          color={getColor()}
          variant='soft'
          value={Math.max(Math.min((used / (limit ?? 100)) * 100, 100), 0)}
          determinate
          size='lg'
        />
      )}
    </div>
  );
};

export const UserPlanWidget = observer((props: IUserPlanWidgetProps): ReactElement => {
  const { t } = useTranslator();

  let contactText: string = '';

  switch (props.planInfo.plan) {
    case Plan.Free:
      contactText = t('purchase_subscription');
      break;
    case Plan.Inactive:
      contactText = t('renew_subscription');
      break;
    case Plan.Admin:
      contactText = '';
      break;
    case Plan.Annotation:
    case Plan.Training:
    default:
      contactText = t('cancel_subscription');
  }

  return (
    <S_Card className='user-plan-widget card-with-list'>
      <SpaceBetweenColumnLayout>
        <div>
          <h5>{t('current_plan')}</h5>
          <div className='list-row'>
            <div className='left'>{t('your_current_plan')}</div>
            <div className='right'>{t(props.planInfo.plan)}</div>
          </div>
          <div className='list-row'>
            <div className='left'>{t('usedSpace')}</div>
            <LimitConsumption
              used={parseFloat(props.usedLimits.usedSpace.toFixed(2))}
              limit={(props.planInfo.availableSpace ?? 0) * 1024}
            />
          </div>
          <div className='list-row'>
            <div className='left'>{t('workspaces_header')}</div>
            <LimitConsumption
              used={props.usedLimits.workspaces}
              limit={props.planInfo.workspaces ?? 0}
            />
          </div>
          <div className='list-row'>
            <div className='left'>{t('workspaceUsers')}</div>
            <LimitConsumption
              used={props.usedLimits.workspaceUsers}
              limit={props.planInfo.workspaceUsers ?? 0}
            />
          </div>
        </div>
        {contactText && (
          <div>
            {contactText} <a href='mailto:acdl-support@zebra.com'>{t('contact_us')}</a>
          </div>
        )}
      </SpaceBetweenColumnLayout>
    </S_Card>
  );
});
